import * as React from 'react';

import {colors} from 'shells/colors/colors';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {LegacySetupTableContainer} from 'shells/table/legacy-setup-table-container';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {Table} from 'shells/table/table';
import {AddButtonRow} from 'shells/table/table-body/add-button-row';
import type {Row} from 'shells/table/types';

import type {ActivityTypeIcon} from 'nutshell-graphql-types';

import {addNotification} from '../../../notifications/index';

import {ActivityTypesRowActionMenu} from './activity-types-row-action-menu';
import {EditCreateActivityTypePopover} from './edit-create-activity-type-popover';
import {useDeleteActivityType} from './graphql/hooks/use-delete-activity-type';
import {useGetActivityTypeDefaults} from './graphql/hooks/use-get-activity-type-defaults';
import {useGetActivityTypes} from './graphql/hooks/use-get-activity-types';
import {getIconForActivityType} from './helpers';
import {PseudoEmailRow} from './pseudo-email-row';

type Props = {
    emptyState?: React.ReactNode;
};

// For legacy reasons we need to show 'email' as a fake first activity type
const emailActivityType = {
    id: 'pseudo-email',
    name: 'Email',
    icon: 'EMAIL',
    isImmutable: true,
    numActivitiesPastYear: null,
    lastActivityTime: null,
    reportUrl: null,
    __isEmailPseudo: true,
} as const;

export function ActivityTypesTable(props: Props) {
    const [isCreating, setIsCreating] = React.useState<boolean>(false);
    const [selectedName, setSelectedName] = React.useState<string | null | undefined>(null);
    const [selectedActivityTypeApiId, setSelectedActivityTypeApiId] = React.useState<
        string | null | undefined
    >(null);
    const [selectedRowIcon, setSelectedRowIcon] = React.useState<
        ActivityTypeIcon | null | undefined
    >(null);

    const buttonRef = React.useRef<HTMLDivElement | null | undefined>(null);

    const {activityTypes, error, isLoading} = useGetActivityTypes();
    const {deleteActivityType} = useDeleteActivityType();
    const {refetch: refetchActivityTypeDefaults} = useGetActivityTypeDefaults();

    const activityTypesWithEmail = [emailActivityType, ...activityTypes];

    const columns = React.useMemo(
        () => [
            {
                id: 'icon',
                Header: 'Icon',
                // @ts-expect-error
                accessor: (row) => row.icon,
                minWidth: 60,
                maxWidth: 60,
                width: 60,
                dataType: 'component',
                disableSortBy: true,
                Cell: ({cell}: any) => {
                    const Icon = getIconForActivityType(cell.value);

                    return (
                        <Icon
                            size={13}
                            color={colors.greyLt}
                            fill={colors.greyLt}
                            style={{color: colors.greyLt}}
                        />
                    );
                },
            },
            {
                id: 'name',
                Header: 'Name',
                minWidth: 350,
                width: 350,
                shouldTruncate: true,
                // @ts-expect-error
                accessor: (row) => ({name: row.name, isPseudo: row.__isEmailPseudo}),
                dataType: 'component',
                Cell: ({cell}: any) => {
                    if (cell.value.isPseudo) {
                        return <PseudoEmailRow />;
                    }

                    return <div title={cell.value.name}>{cell.value.name}</div>;
                },
            },
            {
                id: 'numActivitiesPastYear',
                Header: '# past year',
                minWidth: 120,
                width: 120,
                // @ts-expect-error
                accessor: (row) => row.numActivitiesPastYear,
                dataType: 'integer',
            },
            {
                id: 'lastActivityTime',
                Header: 'Last activity time',
                minWidth: 180,
                width: 180,
                // @ts-expect-error
                accessor: (row) => row.lastActivityTime,
                dataType: 'timestamp',
            },
        ],
        []
    );

    const renderRowActionMenu = (row: Row<any>, rowRef?: HTMLDivElement | null) => {
        if (row.original.__isEmailPseudo) {
            return null;
        }

        return (
            <ActivityTypesRowActionMenu
                onEditActivityType={() => {
                    if (rowRef) {
                        buttonRef.current = rowRef;
                    }

                    setSelectedName(row.original.name);
                    setSelectedActivityTypeApiId(row.original.id);
                    setSelectedRowIcon(row.original.icon);
                    setIsCreating(true);
                }}
                onDeleteActivityType={() => {
                    deleteActivityType({id: row.original.id})
                        .then(() => {
                            refetchActivityTypeDefaults();
                        })
                        .catch(() => {
                            addNotification('Something went wrong', {
                                icon: 'frowny',
                                body: 'We encountered a problem while deleting the activity type.',
                            });
                        });
                }}
                reportUrl={row.original.reportUrl}
                canDelete={!row.original.isImmutable}
            />
        );
    };

    return (
        <LegacySetupTableWrapper title='Activity types' count={activityTypes.length}>
            <LegacySetupTableContainer>
                {isCreating && (
                    <EditCreateActivityTypePopover
                        anchor={buttonRef.current}
                        onClose={() => {
                            setIsCreating(false);
                            setSelectedName(null);
                            setSelectedActivityTypeApiId(null);
                            setSelectedRowIcon(null);
                        }}
                        name={selectedName}
                        activityTypeApiId={selectedActivityTypeApiId}
                        icon={selectedRowIcon}
                    />
                )}
                <Table
                    firstRow={
                        <AddButtonRow
                            getRef={(ref) => {
                                buttonRef.current = ref;
                            }}
                            buttonText='Add activity type'
                            onClick={() => {
                                setIsCreating(true);
                            }}
                        />
                    }
                    // @ts-expect-error
                    renderRowActionMenu={renderRowActionMenu}
                    isStriped={true}
                    isResizable={false}
                    hasHeaderBorder={true}
                    hasHeaderCellBorders={true}
                    headerBackgroundColor='offWhite'
                    isCompact={true}
                    stickyHeaders={false}
                    hasBorders={true}
                    hasCellBorders={false}
                    isCheckboxSelectable={false}
                    fullWidth={true}
                    disableFilters={true}
                    shouldShowFooter={false}
                    // @ts-expect-error
                    columns={columns}
                    data={activityTypesWithEmail}
                    onSelectRow={(row) => {
                        if (row.original.__isEmailPseudo) {
                            return;
                        }

                        setSelectedName(row.original.name);
                        setSelectedActivityTypeApiId(row.original.id);
                        setSelectedRowIcon(row.original.icon);
                        setIsCreating(true);
                    }}
                    getRowRef={(ref) => {
                        buttonRef.current = ref;
                    }}
                    specialStates={[
                        {
                            shouldRender: () => Boolean(error),
                            component: <ErrorState errorMessage={error} />,
                        },
                        {
                            shouldRender: () => Boolean(isLoading),
                            component: <LoadingPage padding={32} />,
                        },
                        {
                            shouldRender: () => !error && !isLoading && !activityTypes.length,
                            component: props.emptyState || <div>No activity types</div>,
                        },
                    ]}
                    hasHoverState={true}
                />
            </LegacySetupTableContainer>
        </LegacySetupTableWrapper>
    );
}
